import type { SalesFunnel } from "@/types/lead";
import type { WorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import {
  humanizeDisplayLabel,
  whatsappIntegrationDisplayLabel,
  type WhatsAppIntegrationLike,
} from "@/lib/whatsapp-integration-label";

function extractChannelPrefix(storedName: string): string {
  const idx = storedName.indexOf(" - ");
  if (idx === -1) return "";
  return storedName.slice(0, idx);
}

function extractNameSuffix(storedName: string): string {
  const idx = storedName.indexOf(" - ");
  if (idx === -1) return storedName;
  return storedName.slice(idx + 3);
}

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Nome exibido do funil (não altera o valor salvo em sales_funnels.name).
 * Com integração vinculada, substitui sufixo técnico (e-mail / instance_name) pelo rótulo da conexão.
 */
export function formatFunnelDisplayName(
  funnel: Pick<SalesFunnel, "name" | "integration_id">,
  integration?: WhatsAppIntegrationLike
): string {
  const stored = funnel.name;

  if (funnel.integration_id && integration) {
    const prefix = extractChannelPrefix(stored) || "WhatsApp";
    const label = whatsappIntegrationDisplayLabel(integration, "");
    if (label) return `${prefix} - ${label}`;

    const phone =
      integration.config &&
      typeof integration.config.phone_number === "string"
        ? integration.config.phone_number.trim()
        : "";
    if (phone) return `${prefix} - ${phone}`;
  }

  const prefix = extractChannelPrefix(stored);
  const suffix = extractNameSuffix(stored);

  if (prefix) {
    if (looksLikeEmail(suffix)) return stored;
    return `${prefix} - ${humanizeDisplayLabel(suffix)}`;
  }

  return humanizeDisplayLabel(stored);
}

export function formatFunnelSelectLabel(
  funnel: Pick<SalesFunnel, "name" | "integration_id" | "is_default">,
  integration?: WhatsAppIntegrationLike
): string {
  const display = formatFunnelDisplayName(funnel, integration);
  return funnel.is_default ? `[Padrão] ${display}` : display;
}

export function resolveFunnelDisplayName(
  funnel: Pick<SalesFunnel, "name" | "integration_id">,
  integrationById?: WorkspaceIntegrationsMap
): string {
  const integration =
    funnel.integration_id && integrationById
      ? integrationById.get(funnel.integration_id)
      : undefined;
  return formatFunnelDisplayName(funnel, integration);
}

export function resolveFunnelSelectLabel(
  funnel: Pick<SalesFunnel, "name" | "integration_id" | "is_default">,
  integrationById?: WorkspaceIntegrationsMap
): string {
  const integration =
    funnel.integration_id && integrationById
      ? integrationById.get(funnel.integration_id)
      : undefined;
  return formatFunnelSelectLabel(funnel, integration);
}
